import {LeadEnum, Signal} from "../../../models"

/**
 * Shared geometry for the Modern ECG stage.
 *
 * <ModernEcgGrid/> draws with these numbers and <ModernCaliperLayer/> measures
 * with them, so they live here rather than in either component. If a caliper
 * and the trace under it disagreed about how many millimetres a pixel is worth,
 * every measurement taken off this screen would be wrong.
 *
 * The plot's x and y units ARE millimetres of ECG paper: one x unit is 1 mm of
 * sweep, one y unit is 1 mm of deflection. That is what makes the conversions
 * at the bottom of this file trivial.
 */

/** Lead arrangements offered by the toolbar's LAYOUT select. */
export type EcgLayoutKey = "12x1" | "6x2" | "3x4"

export const ECG_LAYOUT_KEYS: EcgLayoutKey[] = ["12x1", "6x2", "3x4"];

const L = LeadEnum;

/**
 * Groupings are the clinical standard, copied from the design prototype's
 * `ecg/EcgCanvas.js` LAYOUTS table: 6 x 2 pairs the limb leads with V1–V6,
 * 3 x 4 walks the classic four-column page.
 */
export const ECG_LAYOUTS: {[key in EcgLayoutKey]: {cols: number, rows: LeadEnum[][]}} = {
    "12x1": {
        cols: 1,
        rows: [[L.I], [L.II], [L.III], [L.AVR], [L.AVL], [L.AVF], [L.V1], [L.V2], [L.V3], [L.V4], [L.V5], [L.V6]],
    },
    "6x2": {
        cols: 2,
        rows: [[L.I, L.V1], [L.II, L.V2], [L.III, L.V3], [L.AVR, L.V4], [L.AVL, L.V5], [L.AVF, L.V6]],
    },
    "3x4": {
        cols: 4,
        rows: [[L.I, L.AVR, L.V1, L.V4], [L.II, L.AVL, L.V2, L.V5], [L.III, L.AVF, L.V3, L.V6]],
    },
};

/** Reading order the API returns signals in, used when a lead name does not match. */
export const LEAD_ORDER: LeadEnum[] = [
    L.I, L.II, L.III, L.AVR, L.AVL, L.AVF, L.V1, L.V2, L.V3, L.V4, L.V5, L.V6,
];
export const RHYTHM_LEAD: LeadEnum = L.II;

/**
 * The rhythm lead for a layout, or null where the page needs no strip.
 *
 * @param layout
 */
export function rhythmLeadFor(layout: EcgLayoutKey): LeadEnum | null {
    const definition = ECG_LAYOUTS[layout] === undefined ? ECG_LAYOUTS["12x1"] : ECG_LAYOUTS[layout];

    return definition.cols === 1 ? null : RHYTHM_LEAD
}

export const SPEED_OPTIONS = [12.5, 25, 50];
export const GAIN_OPTIONS = [5, 10, 20];

/*
 * ---------------------------------------------------------------------------
 * The constants below describe the page in millimetres of ECG paper, with
 * {layout: "12x1", speed: 25, amplitude: 10} as the default page and every
 * layout / speed / gain the toolbar offers a variation on it. They say nothing
 * about pixels — that is PX_PER_MM's job, and only its job.
 * ---------------------------------------------------------------------------
 */

/** Left gutter holding the calibration pulse and, in 12 x 1, the lead labels. */
export const LEFT_MARGIN_MM = 20;
/** Breathing room past the last sample so the trace never touches the frame. */
export const RIGHT_PAD_MM = 10;
/** Gap between columns in the multi-column layouts. */
export const COLUMN_GAP_MM = 4;

export const TOP_BASELINE_MM = 170;

export const ROW_MARGIN_MM = 15;

export const ROW_PITCH_MM: {[key in EcgLayoutKey]: {min: number, max: number}} = {
    "12x1": {min: 15, max: 15},
    "6x2": {min: 14, max: 30},
    "3x4": {min: 18, max: 40},
};

const ROW_CLEARANCE_MM = 4;

const PEAK_STRIDE = 4;

const PEAK_PERCENTILE = .75;

/*
 *
 * Calipers are unaffected either way: <ModernCaliperLayer/> derives millimetres
 * per pixel from the rendered rect rather than from any of this, so it follows
 * whatever the figure was actually drawn at.
 */

export const CLASSIC_PAPER_WIDTH_MM = 285;
export const CLASSIC_PAPER_HEIGHT_MM = 195;

export const CLASSIC_PX_PER_MM_X = 1650 / CLASSIC_PAPER_WIDTH_MM;
export const CLASSIC_PX_PER_MM_Y = 1240 / CLASSIC_PAPER_HEIGHT_MM;

const PULSE_ORIGIN_SAMPLE = -370;
const PULSE_SAMPLES = 320;
const PULSE_RISE_SAMPLE = 80;
const PULSE_FALL_SAMPLE = 240;

export const DEFAULT_SAMPLE_RATE = 500;

export const ACQUISITION_SECONDS = 15;
export const PAGE_FRACTION = 17 / 25;

/**
 *
 * @param sampleRate
 */
export function pageSamplesFor(sampleRate: number): number {
    return Math.floor(ACQUISITION_SECONDS * sampleRate * PAGE_FRACTION)
}



export const STRIP_ROWS = 6;

export const STRIP_ROW_PITCH_MM = {min: 20, max: 40};

/**
 * @param ecgData
 */
export function countLeadsWithData(ecgData: Signal[]): number {
    return ecgData.filter(signal => signal && signal.data && signal.data.length > 0).length
}

/**
 *
 * @param sampleRate
 * @param strip
 */
export function windowSamplesFor(sampleRate: number, strip: boolean): number {
    return pageSamplesFor(sampleRate) * (strip ? STRIP_ROWS : 1)
}

/**
 * @param windowStartSec
 * @param sampleRate
 * @param totalSamples
 * @param pageSamples length of a page — see {@link windowSamplesFor}
 */
export function windowStartSampleFor(
    windowStartSec: number,
    sampleRate: number,
    totalSamples: number,
    pageSamples: number,
): number {
    const requested = typeof windowStartSec === "number" && !isNaN(windowStartSec)
        ? Math.round(windowStartSec * sampleRate)
        : 0;
    const maxStart = Math.max(0, totalSamples - pageSamples);

    return Math.min(maxStart, Math.max(0, requested))
}

/**
 * @param geometry
 * @param base baseline of the row, in millimetres, including its initial offset
 * @param height pulse height in millimetres — the gain, so it stays a 1 mV reference
 */
export function computePulse(geometry: EcgGeometry, base: number, height: number): {x: number[], y: number[]} {
    const x: number[] = [];
    const y: number[] = [];

    for (let i = 1; i <= PULSE_SAMPLES; i++) {
        x.push((PULSE_ORIGIN_SAMPLE + i) * geometry.mmPerSample);
        y.push(i > PULSE_RISE_SAMPLE && i < PULSE_FALL_SAMPLE ? base + height : base)
    }

    return {x, y}
}

export type EcgGeometry = {
    cols: number
    rows: LeadEnum[][]
    strip: boolean
    stripRows: number
    sampleRate: number
    totalSamples: number
    pageSamples: number
    windowStartSample: number
    firstSample: number
    windowEndSample: number
    /** Samples drawn in each column. */
    perColumn: number
    /** {@link RHYTHM_LEAD} under the columns, or null in 12 x 1. */
    rhythmLead: LeadEnum | null
    rhythmBaselineMm: number | null
    rhythmSamples: number
    /** Millimetres of paper advanced by one sample at the current speed. */
    mmPerSample: number
    speedScale: number
    columnWidthMm: number
    rowPitchMm: number
    xMin: number
    xMax: number
    xSpanMm: number
    yMin: number
    yMax: number
    ySpanMm: number
    pxPerMmX: number
    pxPerMmY: number
    widthPx: number
    heightPx: number
}

type GeometryInput = {
    ecgData: Signal[]
    layout: EcgLayoutKey
    /** mm/s */
    speed: number
    gain: number
    /**
     */
    pxPerMm?: number
    windowStartSec?: number
    longRecords?: boolean
}

/**
 * How tall the traces on this page actually are, in millimetres of paper.
 *
 * Two deliberate choices, both learned from getting it wrong:
 *
 * 1. Each lead is measured against its OWN mean, not against zero. A lead that
 *    sits on a DC offset is drawn shifted off its baseline by exactly that
 *    offset, so counting it as deflection would report a tall trace where there
 *    is only a displaced flat one.
 *
 * 2. The result is the 75th percentile of the twelve per-lead peaks, not the
 *    largest of them. The largest is the wrong statistic for a uniform row
 *    pitch: one tall precordial R — or one movement artifact — pushed the
 *    measurement straight through the ceiling and spread EVERY row to the
 *    maximum, which is precisely the empty band this was meant to remove. Nine
 *    leads in twelve now sit comfortably; the tallest few may reach into the
 *    row below, which is what a paper cart does as well.
 *
 * Uses the same conversion the grid draws with — `value * (gain / 10) / 100` —
 * so the number describes the trace that will actually be on the page.
 *
 * @param ecgData
 * @param gain mm/mV
 */
function peakDeflectionMm(ecgData: Signal[], gain: number): number {
    const peaks: number[] = [];

    ecgData.forEach(signal => {
        if (!signal || !signal.data || signal.data.length === 0) {
            return
        }

        const end = signal.data.length;
        let total = 0;
        let count = 0;

        for (let i = 1; i < end; i += PEAK_STRIDE) {
            total += signal.data[i];
            count++
        }

        if (count === 0) {
            return
        }

        const mean = total / count;
        let peak = 0;

        for (let i = 1; i < end; i += PEAK_STRIDE) {
            const value = Math.abs(signal.data[i] - mean);

            if (value > peak) {
                peak = value
            }
        }

        peaks.push(peak)
    });

    if (peaks.length === 0) {
        return 0
    }

    peaks.sort((a, b) => a - b);

    return (peaks[Math.floor((peaks.length - 1) * PEAK_PERCENTILE)] * gain) / 1000
}

/**
 * @param input
 */
export function computeEcgGeometry(input: GeometryInput): EcgGeometry {
    const {ecgData, layout, speed, gain} = input;
    const pxPerMmX = input.pxPerMm === undefined ? CLASSIC_PX_PER_MM_X : input.pxPerMm;
    const pxPerMmY = input.pxPerMm === undefined ? CLASSIC_PX_PER_MM_Y : input.pxPerMm;
    const definition = ECG_LAYOUTS[layout] || ECG_LAYOUTS["12x1"];

    const reference = ecgData[0];
    const sampleRate = (reference && reference.sampleRate) || DEFAULT_SAMPLE_RATE;
    const totalSamples = reference && reference.data ? reference.data.length : 0;

    const strip = input.longRecords === true && countLeadsWithData(ecgData) === 1;
    const cols = strip ? 1 : definition.cols;
    const rows: LeadEnum[][] = strip ? [] : definition.rows;
    const rowSamples = pageSamplesFor(sampleRate);
    const pageSamples = windowSamplesFor(sampleRate, strip);
    const windowStartSample = windowStartSampleFor(
        input.windowStartSec === undefined ? 0 : input.windowStartSec,
        sampleRate,
        totalSamples,
        pageSamples,
    );
    const firstSample = Math.max(1, windowStartSample);
    const windowEndSample = Math.min(totalSamples, windowStartSample + pageSamples);
    const drawable = Math.max(0, windowEndSample - firstSample);
    const stripRows = strip ? Math.max(1, Math.min(STRIP_ROWS, Math.ceil(drawable / rowSamples))) : 0;
    const perColumn = strip
        ? Math.max(1, Math.min(rowSamples, drawable))
        : Math.max(1, Math.floor(drawable / cols));

    const speedScale = speed / 25;
    const mmPerSample = speed / sampleRate;
    const leftMarginMm = LEFT_MARGIN_MM * speedScale;
    const columnWidthMm = perColumn * mmPerSample;
    const traceWidthMm = (cols * columnWidthMm) + ((cols - 1) * COLUMN_GAP_MM);

    const xMin = -leftMarginMm;

    const xMax = traceWidthMm + RIGHT_PAD_MM;
    const xSpanMm = xMax - xMin;
    const layoutBounds = ROW_PITCH_MM[layout] === undefined ? ROW_PITCH_MM["12x1"] : ROW_PITCH_MM[layout];
    const bounds = strip ? STRIP_ROW_PITCH_MM : layoutBounds;
    const neededPitchMm = (2 * peakDeflectionMm(ecgData, gain)) + ROW_CLEARANCE_MM;
    const rowPitchMm = Math.min(bounds.max, Math.max(bounds.min, neededPitchMm));
    const rhythmLead = strip || cols === 1 ? null : RHYTHM_LEAD;
    const rowCount = strip ? stripRows : rows.length + (rhythmLead === null ? 0 : 1);
    const bottomBaselineMm = TOP_BASELINE_MM - (Math.max(0, rowCount - 1) * rowPitchMm);
    const rhythmBaselineMm = rhythmLead === null ? null : bottomBaselineMm;
    const yMax = TOP_BASELINE_MM + ROW_MARGIN_MM;
    const yMin = bottomBaselineMm - ROW_MARGIN_MM;
    const ySpanMm = yMax - yMin;

    return {
        cols,
        rows,
        strip,
        stripRows,
        sampleRate,
        totalSamples,
        pageSamples,
        windowStartSample,
        firstSample,
        windowEndSample,
        perColumn,
        rhythmLead,
        rhythmBaselineMm,
        rhythmSamples: perColumn * cols,
        mmPerSample,
        speedScale,
        columnWidthMm,
        rowPitchMm,
        xMin,
        xMax,
        xSpanMm,
        yMin,
        yMax,
        ySpanMm,
        pxPerMmX,
        pxPerMmY,
        widthPx: Math.round(xSpanMm * pxPerMmX),
        heightPx: Math.round(ySpanMm * pxPerMmY),
    }
}

/**
 * Millimetres of sweep -> milliseconds, at the given paper speed.
 *
 * @param millimetres
 * @param speed mm/s
 */
export function millimetresToMs(millimetres: number, speed: number): number {
    return (millimetres / speed) * 1000
}

/**
 * Millimetres of deflection -> millivolts, at the given gain.
 *
 * @param millimetres
 * @param gain mm/mV
 */
export function millimetresToMillivolts(millimetres: number, gain: number): number {
    return millimetres / gain
}
